import { useState, type FormEvent } from 'react'
import { authConfigured, useAuth, type OAuthProvider } from '../../lib/auth'
import { useT } from '../../lib/i18n'
import LocalePicker from '../../components/LocalePicker'
import Wordmark from '../../components/Wordmark'
import styles from './Splash.module.css'

/**
 * The public face of heimar.ai — the way in.
 *
 * Signing up and signing in are the same act, and neither is gated: anyone may
 * make an account, sign in, and look at what is on the shelf. Being let in to
 * PLAY is a separate decision a person makes later (see AccessControl's tiers).
 * So this page asks for nothing and promises nothing — it is a door, not a form.
 *
 * ⚠ COPY IS PLACEHOLDER — the founder writes the real thing.
 */

/**
 * The doors, in the order they are offered.
 *
 * ⚠ EACH MUST BE ENABLED IN THE SUPABASE DASHBOARD (Authentication → Providers).
 * A provider listed here but not enabled there fails at the moment of the click
 * with a Supabase error, which is a bad first impression — so trim this list to
 * what is actually switched on rather than leaving hopeful entries in it.
 *
 * Google first because it is the widest. Discord second because the alpha runs
 * there, so for most testers it is one click from where they already are.
 */
const PROVIDERS: { id: OAuthProvider; label: string }[] = [
  { id: 'google', label: 'Google' },
  { id: 'discord', label: 'Discord' },
  { id: 'apple', label: 'Apple' },
]

/**
 * Monochrome provider marks.
 *
 * ⚠ Google and Apple both publish sign-in button guidelines (colour, spacing,
 * exact wording) that these do not follow — they follow the house style instead,
 * because a page this austere cannot carry three brand-coloured buttons. Fine
 * for a closed alpha; worth revisiting before any public launch.
 */
function ProviderMark({ id }: { id: OAuthProvider }) {
  const common = { width: 15, height: 15, viewBox: '0 0 24 24', 'aria-hidden': true }

  if (id === 'apple')
    return (
      <svg {...common} fill="currentColor">
        <path d="M16.4 12.8c0-2.4 2-3.6 2.1-3.6-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.6.9-.8 0-1.9-.9-3.1-.8-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.5.8 1.2 1.8 2.4 3 2.4 1.2 0 1.6-.8 3.1-.8 1.4 0 1.8.8 3.1.7 1.3 0 2.1-1.2 2.9-2.3.9-1.3 1.3-2.6 1.3-2.7-.1 0-2.5-1-2.6-3.8zM14 5.2c.7-.8 1.1-2 1-3.2-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.3z" />
      </svg>
    )

  if (id === 'discord')
    return (
      <svg {...common} fill="currentColor">
        <path d="M20.3 4.9A19.8 19.8 0 0015.4 3.4a13.8 13.8 0 00-.7 1.4 18.3 18.3 0 00-5.5 0 13.6 13.6 0 00-.7-1.4A19.7 19.7 0 003.7 4.9C.6 9.5-.3 14 .2 18.4a19.9 19.9 0 006 3.1c.5-.7.9-1.4 1.3-2.2a13 13 0 01-2-1c.2-.1.3-.2.5-.4a14.2 14.2 0 0012.1 0l.5.4c-.7.4-1.3.7-2 1 .4.8.8 1.5 1.3 2.2a19.9 19.9 0 006-3.1c.6-5.1-.9-9.6-3.6-13.5zM8 15.7c-1.2 0-2.2-1.1-2.2-2.4C5.8 12 6.8 11 8 11s2.2 1.1 2.2 2.3c0 1.3-1 2.4-2.2 2.4zm8 0c-1.2 0-2.2-1.1-2.2-2.4 0-1.3 1-2.3 2.2-2.3s2.2 1.1 2.2 2.3c0 1.3-1 2.4-2.2 2.4z" />
      </svg>
    )

  return (
    <svg {...common} fill="currentColor">
      <path d="M12 10.2v3.9h5.5a4.7 4.7 0 01-2 3.1v2.6h3.3c1.9-1.8 3-4.4 3-7.5 0-.7-.1-1.4-.2-2.1H12z" />
      <path d="M12 22c2.7 0 5-.9 6.7-2.4l-3.3-2.6c-.9.6-2.1 1-3.4 1-2.6 0-4.8-1.7-5.6-4.1H3v2.6A10 10 0 0012 22z" />
      <path d="M6.4 13.9a6 6 0 010-3.8V7.5H3a10 10 0 000 9l3.4-2.6z" />
      <path d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.9A10 10 0 003 7.5l3.4 2.6C7.2 7.7 9.4 5.9 12 5.9z" />
    </svg>
  )
}

export default function Splash() {
  const { signIn, signInWith } = useAuth()
  const t = useT()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [showEmail, setShowEmail] = useState(false)

  async function handoff(provider: OAuthProvider) {
    setBusy(provider)
    setError('')
    // On success the browser leaves for the provider and nothing below runs, so
    // `busy` is only ever cleared on the failure path.
    const res = await signInWith(provider)
    if (!res.ok) {
      setError(res.error ?? t('splash.doorDidNotOpen'))
      setBusy(null)
    }
  }

  async function submitEmail(ev: FormEvent) {
    ev.preventDefault()
    const address = email.trim()
    if (!address) return
    setBusy('email')
    setError('')
    try {
      const res = await signIn(address)
      if (!res.ok && authConfigured) {
        setError(res.error ?? t('splash.couldNotSend'))
        return
      }
      setSent(true)
    } catch {
      setError(t('splash.wentWrong'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <Wordmark className={styles.mark} />

        {/*
          IN THE PATH, not in a corner (founder-ruled 2026-08-21). Between knowing what this
          is and choosing how to get in — which is exactly when the language matters, and it
          sits under a gloss written in the language it selects, so the control shows its own
          effect the moment it is used.
        */}
        <LocalePicker variant="prominent" className={styles.locale} />

        {sent ? (
          <p className={styles.quiet}>{t('splash.linkOnWay', { email: email.trim() })}</p>
        ) : (
          <>
            <div className={styles.doors}>
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  className={styles.door}
                  disabled={busy !== null}
                  onClick={() => void handoff(p.id)}
                >
                  <ProviderMark id={p.id} />
                  <span>
                    {busy === p.id
                      ? t('splash.opening')
                      : t('splash.continueWith', { provider: p.label })}
                  </span>
                </button>
              ))}
            </div>

            {/*
              The email path is kept, and kept SECOND. It is the only door for
              someone with none of the three accounts above — but it costs a
              round-trip through an inbox, so it should not be the first thing
              offered.
            */}
            {showEmail ? (
              <form className={styles.form} onSubmit={submitEmail}>
                <input
                  className={styles.input}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('splash.emailPlaceholder')}
                  aria-label={t('splash.emailLabel')}
                  autoComplete="email"
                  autoFocus
                  required
                />
                <button
                  className={styles.button}
                  type="submit"
                  disabled={busy !== null}
                >
                  {busy === 'email' ? '…' : t('splash.sendLink')}
                </button>
              </form>
            ) : (
              <button
                className={styles.alt}
                onClick={() => setShowEmail(true)}
              >
                {t('splash.orEmail')}
              </button>
            )}

            <p className={styles.error} role="alert">
              {error}
            </p>

            {!authConfigured && (
              <p className={styles.unconfigured}>
                {t('splash.notConfigured')}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
